import React, { useState, useEffect } from "react";
import { BiDownload, BiFilter, BiRefresh } from "react-icons/bi";
import { useSalesReport } from "../../../hooks";
import { formatCurrency, formatNumber } from "../../../utils/formatters";
import {
  ReportFilters,
  ReportTabs,
  KPICard,
  DataTable,
  ExportModal,
} from "./shared";

const SalesReport = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const [showFilters, setShowFilters] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [filters, setFilters] = useState({
    period: "month",
    startDate: null,
    endDate: null,
  });

  const {
    data: summary,
    loading,
    error,
    fetchDetails,
    fetchTopSelling,
    fetchByHour,
    exportData,
  } = useSalesReport(filters);

  const [details, setDetails] = useState({ data: [], pagination: {} });
  const [topSelling, setTopSelling] = useState([]);
  const [salesByHour, setSalesByHour] = useState([]);

  useEffect(() => {
    loadTabData();
  }, [activeTab, filters]);

  const loadTabData = async () => {
    try {
      switch (activeTab) {
        case "details":
          const detailsResult = await fetchDetails({ ...filters, page: 1 });
          setDetails(detailsResult);
          break;
        case "top":
          const topResult = await fetchTopSelling(filters);
          setTopSelling(topResult || []);
          break;
        case "hourly":
          const hourlyResult = await fetchByHour(filters);
          setSalesByHour(hourlyResult || []);
          break;
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
    }
  };

  const handlePageChange = async (page) => {
    const result = await fetchDetails({ ...filters, page });
    setDetails(result);
  };

  const handleExport = async (format) => {
    try {
      const data = await exportData(format, filters);
      // Télécharger le fichier
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `rapport-ventes.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Erreur export:", error);
    }
  };

  const tabs = [
    { id: "summary", label: "Résumé" },
    { id: "details", label: "Détails" },
    { id: "top", label: "Top ventes" },
    { id: "hourly", label: "Par heure" },
  ];

  const columns = {
    details: [
      { key: "code", label: "Code" },
      {
        key: "clientName",
        label: "Client",
        render: (row) => row.clientName || "Anonyme",
      },
      {
        key: "amount",
        label: "Montant",
        render: (row) => formatCurrency(row.amount),
      },
      { key: "method", label: "Paiement" },
      {
        key: "services",
        label: "Services",
        render: (row) => row.services.map((s) => s.name).join(", "),
      },
      {
        key: "createdAt",
        label: "Date",
        render: (row) => new Date(row.createdAt).toLocaleDateString("fr-FR"),
      },
    ],
    top: [
      { key: "serviceName", label: "Service" },
      {
        key: "quantity",
        label: "Quantité",
        render: (row) => formatNumber(row.quantity),
      },
      {
        key: "revenue",
        label: "Chiffre d'affaires",
        render: (row) => formatCurrency(row.revenue),
      },
    ],
    hourly: [
      { key: "hour", label: "Heure", render: (row) => `${row.hour}h` },
      {
        key: "sales",
        label: "Nombre de ventes",
        render: (row) => formatNumber(row.sales),
      },
      {
        key: "revenue",
        label: "CA",
        render: (row) => formatCurrency(row.revenue),
      },
    ],
  };

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        Une erreur est survenue : {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête du rapport */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-poppins font-semibold text-[20px] text-title">
          Rapport des ventes
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiFilter size={18} />
            Filtres
          </button>
          <button
            onClick={() => setShowExport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors"
          >
            <BiDownload size={18} />
            Exporter
          </button>
          <button
            onClick={loadTabData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiRefresh size={18} />
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Onglets */}
      <ReportTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Contenu */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
        </div>
      ) : (
        <>
          {activeTab === "summary" && summary && (
            <div className="space-y-6">
              {/* KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                  title="Chiffre d'affaires"
                  value={formatCurrency(summary.overview.totalRevenue)}
                  icon="💰"
                />
                <KPICard
                  title="Nombre de ventes"
                  value={formatNumber(summary.overview.totalSales)}
                  icon="📊"
                />
                <KPICard
                  title="Ticket moyen"
                  value={formatCurrency(summary.overview.averageTicket)}
                  icon="🎫"
                />
                <KPICard
                  title="Ventes en ligne"
                  value={formatCurrency(summary.overview.onlineSales)}
                  icon="🌐"
                />
              </div>

              {/* Graphiques à ajouter avec Recharts ou Chart.js */}
              {summary.byService && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="font-poppins font-semibold text-[16px] mb-4">
                    Ventes par service
                  </h3>
                  {/* Graphique ici */}
                </div>
              )}
            </div>
          )}

          {activeTab === "details" && (
            <DataTable
              columns={columns.details}
              data={details.data}
              pagination={details.pagination}
              onPageChange={handlePageChange}
            />
          )}

          {activeTab === "top" && (
            <DataTable columns={columns.top} data={topSelling} />
          )}

          {activeTab === "hourly" && (
            <DataTable columns={columns.hourly} data={salesByHour} />
          )}
        </>
      )}

      {/* Modal d'export */}
      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          onExport={handleExport}
        />
      )}
    </div>
  );
};

export default SalesReport;
